import React from "react";
import {
  StyleSheet,
  TouchableOpacity,
  View,
  type StyleProp,
  type ViewStyle,
  Platform,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { useThemeColor } from "@/hooks/useThemeColor";

export interface IFilterChipProps {
  /** Text label displayed inside the chip */
  label: string;
  /** Whether the chip is currently selected / active */
  selected?: boolean;
  /** Callback triggered when chip is tapped */
  onPress: () => void;
  /** Optional icon name from Ionicons, or custom React element */
  icon?: keyof typeof Ionicons.glyphMap | React.ReactNode;
  /** Optional badge count number */
  count?: number;
  /** Optional custom active color tint */
  activeColor?: string;
  /** Compact density (smaller padding and typography) */
  compact?: boolean;
  /** Container style override */
  style?: StyleProp<ViewStyle>;
  /** Test identifier */
  testID?: string;
  /** Disabled state */
  disabled?: boolean;
}

/**
 * Standardized Filter Chip component for the unified filtering system.
 * Used across MemoryFilter, MediaFilter, ContactFilter, GroupFilter, ShareFilter,
 * TagFilter, ActivityFilter, and MessageFilter.
 */
export function FilterChip({
  label,
  selected = false,
  onPress,
  icon,
  count,
  activeColor,
  compact = false,
  style,
  testID,
  disabled = false,
}: IFilterChipProps): React.JSX.Element {
  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const defaultTint = useThemeColor({}, "tint");
  const textMuted = useThemeColor({}, "textMuted");
  const buttonSecondaryBg = useThemeColor({}, "buttonSecondary");

  const effectiveTint = activeColor || defaultTint;

  return (
    <TouchableOpacity
      testID={testID}
      activeOpacity={0.7}
      disabled={disabled}
      onPress={onPress}
      accessibilityRole="checkbox"
      accessibilityState={{ checked: selected }}
      accessibilityLabel={`${label}${count !== undefined ? ` (${count})` : ""}`}
      style={[
        styles.chip,
        compact && styles.compactChip,
        {
          backgroundColor: selected ? `${effectiveTint}1A` : buttonSecondaryBg || cardBg,
          borderColor: selected ? effectiveTint : borderColor,
        },
        disabled && styles.disabled,
        style,
      ]}
    >
      {/* Optional leading icon */}
      {icon && (
        <View style={styles.iconContainer}>
          {typeof icon === "string" ? (
            <Ionicons
              name={icon as any}
              size={compact ? 12 : 14}
              color={selected ? effectiveTint : textMuted}
            />
          ) : (
            icon
          )}
        </View>
      )}

      {/* Label text */}
      <ThemedText
        style={[
          styles.label,
          compact && styles.compactLabel,
          {
            color: selected ? effectiveTint : undefined,
            fontWeight: selected ? "600" : "400",
          },
        ]}
        numberOfLines={1}
      >
        {label}
      </ThemedText>

      {/* Optional count badge */}
      {count !== undefined && (
        <View
          style={[
            styles.countBadge,
            {
              backgroundColor: selected ? effectiveTint : `${textMuted}33`,
            },
          ]}
        >
          <ThemedText
            style={[
              styles.countText,
              { color: selected ? "#FFFFFF" : textMuted },
            ]}
          >
            {count}
          </ThemedText>
        </View>
      )}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  chip: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
    marginRight: 6,
    marginBottom: 6,
    ...Platform.select({
      web: {
        cursor: "pointer",
        userSelect: "none",
        transition: "all 0.15s ease-in-out",
      },
    }),
  },
  compactChip: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 4,
    marginBottom: 4,
  },
  iconContainer: {
    marginRight: 5,
    alignItems: "center",
    justifyContent: "center",
  },
  label: {
    fontSize: 13,
  },
  compactLabel: {
    fontSize: 11,
  },
  countBadge: {
    marginLeft: 6,
    paddingHorizontal: 6,
    paddingVertical: 1,
    borderRadius: 10,
    minWidth: 18,
    alignItems: "center",
    justifyContent: "center",
  },
  countText: {
    fontSize: 10,
    fontWeight: "700",
  },
  disabled: {
    opacity: 0.5,
    ...Platform.select({
      web: {
        cursor: "not-allowed",
      },
    }),
  },
});
